import React, { useEffect, useState } from 'react';
import { apiRequest } from '../../../../service/apiRequest';
import {
  Table,
  Button,
  Flex,
  Breadcrumb,
  Form,
  message,
  Popconfirm,
  Space,
  Pagination,
  Switch,
  TableProps,
} from 'antd';
import { createStyles } from 'antd-style';
import { HomeOutlined, DeleteOutlined, EditOutlined } from '@ant-design/icons';
import { theme } from 'antd';
import ProductModal from './ProductModal';
import { showSuccess, showError } from '../../../../components/errorHandler';
import type { PopconfirmProps, TableColumnsType } from 'antd';
import { useCurrentModule } from '../../../../hooks/useCurrentModule';

import { useSearchParams } from 'react-router-dom';
import CopyButton from '../../../../components/copyButton';
import Barcode from '../../../../components/Barcode';
interface BarcodeProps {
  value: string; // the product_code to encode
}
const useStyle = createStyles(({ css }) => ({
  customTable: css`
    .ant-table {
      &-container {
        .ant-table-body,
        .ant-table-content {
          scrollbar-width: thin;
          scrollbar-color: #eaeaea transparent;
        }
      }
    }
  `,
}));

interface Products {
  product_id: number;
  product_name: string;
  product_code: string;
  product_price: number;
  product_status: number;
}
const Products: React.FC = () => {
  const [products, setProducts] = useState<Products[]>([]);
  const [form] = Form.useForm();
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [initialValues, setInitialValues] = useState<Products | null>(null);
  const { styles } = useStyle();
  const [pagination, setPagination] = useState<{ current: number; pageSize: number; total: number }>({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const currentModule = useCurrentModule();

  const [filteredInfo, setFilteredInfo] = useState<Record<string, any>>({});
  const [sortedInfo, setSortedInfo] = useState<any>({});

  const [searchParams, setSearchParams] = useSearchParams();

  const voucherType = searchParams.get('voucher_type') || '';


  const cancel: PopconfirmProps['onCancel'] = (e) => {
    console.log(e);
    showError('Click on No');
  };

  const {
    token: { colorBgContainer, borderRadiusLG },
  } = theme.useToken();

  const fetchProduct = async (
    page = 1,
    limit = 10,
    voucher_type: string | null = null,
    voucher_date: string | null = null) => {
    try {
      const response = await apiRequest<{
        data: Products[];
        page: number;
        limit: number;
        total: number;
      }>({
        method: 'get',
        url: `products?page=${page}&limit=${limit}`,
        data: {
          voucher_type: voucherType
        }
      });
      console.log("data", response.data);
      setProducts(response.data);
      setPagination({
        current: response.page,
        pageSize: response.limit,
        total: response.total,
      });
    } catch {
      showError('Failed to fetch vouchers');
    }
  };



  useEffect(() => {
    fetchProduct(pagination.current,
      pagination.pageSize);
  }, [searchParams]);

  const handleEditClick = (record: Products) => {
    setIsEditMode(true);
    setInitialValues({
      product_id: record.product_id,
      product_name: record.product_name,
      product_status: record.product_status,
      product_code: record.product_code,
      product_price: record.product_price,
    });
    setIsModalVisible(true);
  };

  const handleCreate = () => {
    fetchProduct();
  };

  const showCreateModal = () => {
    setIsEditMode(false);
    setInitialValues(null);
    setIsModalVisible(true);
  };

  const hideCreateModal = () => {
    setIsModalVisible(false);
    setIsEditMode(false);
    setInitialValues(null);
  };

  const handleDelete = async (product_id: number) => {
    try {
      await apiRequest({
        method: 'delete',
        url: `/delete-product/${product_id}`,
      });
      showSuccess('Product deleted');
      fetchProduct();
    } catch {
      showError('Failed to delete Product');
    }
  };

  const handleStatusToggle = async (productId: number) => {
    setProducts((prev) =>
      prev.map((item) =>
        item.product_id === productId
          ? { ...item, product_status: item.product_status === 1 ? 0 : 1 }
          : item
      )
    );

    try {
      await apiRequest({
        method: 'put',
        url: `/update-status-product/${productId}`,
      });
      showSuccess('Product status updated');
    } catch (error) {
      showError('Failed to update status');
      setProducts((prev) =>
        prev.map((item) =>
          item.product_id === productId
            ? { ...item, product_status: item.product_status === 1 ? 0 : 1 }
            : item
        )
      );
    }
  };

  const handleTableChange: TableProps<Products>['onChange'] = (pagination, filters, sorter) => {
    setFilteredInfo(filters);
    setSortedInfo(sorter);
  };




  const columns: TableColumnsType<Products> = [
    {
      title: 'Product Name',
      dataIndex: 'product_name',
      key: 'product_name',
      filters: Array.from(new Set(products.map((p) => p.product_name))).map((name) => ({
        text: name,
        value: name,
      })),
      filterSearch: true,
      filteredValue: filteredInfo.product_name || null,
      onFilter: (value, record) => record.product_name.includes(value as string),
      sorter: (a, b) => a.product_name.localeCompare(b.product_name),
      sortOrder: sortedInfo.columnKey === 'product_name' ? sortedInfo.order : null,
      sortDirections: ['ascend', 'descend'],
      ellipsis: true,
    },
    {
      title: 'Product Code',
      dataIndex: 'product_code',
      key: 'product_code',
      render: (_, record) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <span>{record.product_code || 'N/A'}</span>
          {record.product_code && <CopyButton textToCopy={record.product_code} />}
        </div>
      ),
    },
    {
      title: 'Bar Code',
      dataIndex: 'product_code',
      key: 'product_code',
      render: (_, record) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          {record.product_code && <Barcode value={record.product_code} />}
        </div>
      ),
    },
    {
      title: 'Price',
      dataIndex: 'product_price',
      key: 'product_price',
      sorter: (a, b) => a.product_price - b.product_price,
      sortOrder: sortedInfo.columnKey === 'product_price' ? sortedInfo.order : null,
      render: (_, record) => <span>₹{record.product_price.toFixed(2)}</span>,
    },
    {
      title: 'Status',
      dataIndex: 'product_status',
      key: 'product_status',
      filters: [
        { text: 'Active', value: 1 },
        { text: 'Inactive', value: 0 },
      ],
      filteredValue: filteredInfo.product_status || null,
      onFilter: (value, record) => record.product_status === value,
      render: (_, record) => (
        <span style={{ color: record.product_status ? 'green' : 'red' }}>
          {record.product_status ? 'Active' : 'Inactive'}
        </span>
      ),
    },
  ];


  // Conditionally add action column
  if (currentModule?.right_edit === 'Y' || currentModule?.right_delete === 'Y') {
    columns.push({
      title: 'Action',
      width: 200,
      render: (_: any, record: Products) => (
        <Space size="middle">
          {currentModule?.right_edit === 'Y' && (
            <>
              <Switch
                checkedChildren="Active"
                unCheckedChildren="Inactive"
                checked={!!record.product_status}
                onChange={() => handleStatusToggle(record.product_id)}
              />
              <Button onClick={() => handleEditClick(record)} className="px-8">
                <EditOutlined />
              </Button>
            </>
          )}
          {currentModule?.right_delete === 'Y' && (
            <Popconfirm
              title="Delete this product?"
              onConfirm={() => handleDelete(record.product_id)}
              onCancel={cancel}
              okText="Yes"
              cancelText="No"
            >
              <Button danger className="px-8">
                <DeleteOutlined />
              </Button>
            </Popconfirm>
          )}
        </Space>
      ),
    });
  }


  return (
    <>
      <Space
        style={{
          display: 'flex',
          marginBottom: 16,
          alignItems: 'center',
          width: '100%',
          justifyContent: 'space-between',
        }}
      >
        <Breadcrumb style={{ display: 'inline-block', margin: 0 }}>
          <Breadcrumb.Item href="/">
            <HomeOutlined />
          </Breadcrumb.Item>
          <Breadcrumb.Item>View Products</Breadcrumb.Item>
        </Breadcrumb>

        {currentModule?.right_add === 'Y' && (
          <Flex gap="small">
            <Button type="primary" onClick={showCreateModal}>
              Add Product
            </Button>
          </Flex>
        )}
      </Space>


      <div
        style={{
          minHeight: 360,
          background: colorBgContainer,
          borderRadius: borderRadiusLG,
        }}
      >
        <Table<Products>
          tableLayout="auto"
          className={`${styles.customTable} scrollable-table`}
          columns={columns}
          onChange={handleTableChange}
          rowKey="voucher_id"
          dataSource={products.slice(
            (pagination.current - 1) * pagination.pageSize,
            pagination.current * pagination.pageSize
          )}
          bordered
          pagination={false}
          scroll={{ y: 37 * 15 }}
          locale={{ emptyText: 'No product found' }}
          footer={() => (
            <Flex justify="space-between">
              <span>
                Showing {(pagination.current - 1) * pagination.pageSize + 1}–
                {Math.min(pagination.current * pagination.pageSize, products.length)} of{' '}
                {products.length}
              </span>
              <Pagination
                current={pagination.current}
                pageSize={pagination.pageSize}
                total={pagination.total}
                showSizeChanger
                pageSizeOptions={['10', '20', '50', '100']}
                onChange={(page, pageSize) => {
                  setPagination((prev) => ({ ...prev, current: page, pageSize }));
                  fetchProduct(page, pageSize);
                }}
              />
            </Flex>
          )}
        />


      </div>

      <ProductModal
        visible={isModalVisible}
        onCancel={hideCreateModal}
        onCreate={handleCreate}
        isEditMode={isEditMode}
        initialValues={initialValues}
      />
    </>
  );
};

export default Products;